/**
 * Turns the two free-text refusals the table editor's UPDATE call can get back from the backend
 * into the list of column names they are about, so the Columns tab grid can mark those rows.
 * Deterministic and culture-invariant: no `toLocaleLowerCase`, no `Intl`, plain `toLowerCase()`.
 */

/** Escapes regex metacharacters so a literal template fragment can be embedded in a pattern. */
const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The four localized KV1083 ("Column name {0} is reserved key...") templates from
 * Backend/Kuika.Common/Exceptions/KExceptionMessages.cs. Kept as data so each matcher is derived
 * from the same text the backend actually emits, instead of a hand-duplicated regex that can
 * drift from it. Order: English, Turkish, French, Chinese (Simplified).
 */
const RESERVED_COLUMN_TEMPLATES: readonly string[] = [
  "Column name {0} is reserved key. Please enter another name.",
  "Kolon için {0} adı kullanılamaz. Lütfen başka bir ad giriniz.",
  "Le nom de colonne {0} est une clé réservée. Veuillez entrer un autre nom.",
  "列名 {0} 是保留关键字。请输入其他名称。"
];

/**
 * Builds a per-line matcher for one KV1083 template: the literal text around `{0}` is escaped
 * and its runs of spaces are relaxed to `\s+` (minor transport whitespace differences still
 * match), the placeholder becomes a non-greedy capture, and the match is left unanchored so an
 * embedded or prefixed line still matches. Because the literal text differs from the sibling
 * KV1082 ("Table name {0} ...") template in every language, this can never match a table line.
 */
const buildColumnMatcher = (template: string): RegExp => {
  const placeholder: string = "{0}";
  const placeholderIndex: number = template.indexOf(placeholder);
  const prefix: string = template.slice(0, placeholderIndex);
  const suffix: string = template.slice(placeholderIndex + placeholder.length);
  const relax = (text: string): string => escapeRegExp(text).replace(/ +/g, "\\s+");
  return new RegExp(`${relax(prefix)}(.+?)${relax(suffix)}`);
};

const RESERVED_COLUMN_MATCHERS: readonly RegExp[] = RESERVED_COLUMN_TEMPLATES.map(buildColumnMatcher);

/**
 * Column names a reserved-word refusal (KV1083) lists, in message order, deduped. The backend
 * builds the message with one `AppendLine` per refusal kind (table, then column), so the message
 * may also contain an unrelated table line (KV1082) — that line is simply never matched by any
 * of the column matchers. Empty when the message is not a reserved-column refusal at all.
 */
export const parseReservedColumnNames = (message: string | null | undefined): string[] => {
  if (!message) {
    return [];
  }
  const lines: string[] = message.split(/\r?\n/);
  const names: string[] = [];
  for (const rawLine of lines) {
    const line: string = rawLine.trim();
    if (!line) {
      continue;
    }
    for (const matcher of RESERVED_COLUMN_MATCHERS) {
      const match: RegExpMatchArray | null = line.match(matcher);
      if (match) {
        const captured: string = match[1];
        for (const part of captured.split(",")) {
          const name: string = part.trim();
          if (name && !names.includes(name)) {
            names.push(name);
          }
        }
        break;
      }
    }
  }
  return names;
};

/** A raw name candidate pulled out of a migration error message, tagged with where it was found. */
interface IRawCandidate {
  readonly index: number;
  readonly text: string;
}

/**
 * "column" and its localized equivalents (Turkish "sütun"/"kolon", French "colonne", German
 * "Spalte") — the keyword that, followed by a quoted identifier a few words later, introduces the
 * offending column in most relational-database driver error strings.
 */
const KEYWORD_FILLER: string = String.raw`(?:column|s[uü]tun|kolon|colonne|spalte)(?:\s+[\p{L}]+){0,3}\s*:?\s*`;

const BACKTICK: string = "`";

/**
 * A keyword (optionally followed by a short filler like " name " or " non valide : ") immediately
 * before a quoted identifier, one regex per quote style the drivers use: '…', "…", `…`, […].
 * Covers SQL Server ("Invalid column name 'X'.", "Cannot insert the value NULL into column 'X'"),
 * Postgres (`column "x" does not exist`) and localized variants (French "Nom de colonne non
 * valide : 'X'.", Turkish "Geçersiz kolon adı 'X'.", German "Spalte 'X' existiert nicht.").
 */
const KEYWORD_QUOTE_PATTERNS: readonly RegExp[] = [
  new RegExp(String.raw`${KEYWORD_FILLER}'([^']*)'`, "giu"),
  new RegExp(String.raw`${KEYWORD_FILLER}"([^"]*)"`, "giu"),
  new RegExp(`${KEYWORD_FILLER}${BACKTICK}([^${BACKTICK}]*)${BACKTICK}`, "giu"),
  new RegExp(String.raw`${KEYWORD_FILLER}\[([^\]]*)\]`, "giu")
];

/**
 * Oracle's dotted `"SCHEMA"."TABLE"."COLUMN"` identifier path, standalone (no "column" keyword
 * required — e.g. `ORA-01400: cannot insert NULL into ("APP"."ORDERS"."NAME")`). Captures only the
 * last quoted segment, regardless of how many segments precede it.
 */
const ORACLE_DOTTED_PATH_PATTERN: RegExp = /(?:"[^"]+"\.)+"([^"]+)"/gi;

/** Oracle's `ORA-00904: "X": invalid identifier`. */
const ORACLE_INVALID_IDENTIFIER_PATTERN: RegExp = /"([^"]+)"\s*:\s*invalid identifier/gi;

/**
 * SQL Server's unquoted `ALTER TABLE ALTER COLUMN X failed because one or more objects access
 * this column.` — X may be `[bracketed]`; the brackets are stripped afterwards.
 */
const SQLSERVER_ALTER_COLUMN_PATTERN: RegExp = /ALTER\s+TABLE\s+ALTER\s+COLUMN\s+(\[?[A-Za-z0-9_]+\]?)\s+failed/gi;

/**
 * Postgres unique-violation detail `Key (a, b)=(1, 2) is duplicated.` / `Key (x)=(y) already
 * exists.` — the column list sits inside the first parenthesised group after "Key".
 */
const POSTGRES_KEY_DUPLICATE_PATTERN: RegExp = /Key\s*\(([^)]*)\)/gi;

/** Strips a single layer of `[...]` wrapping, when both ends are present. */
const stripWrappingBrackets = (text: string): string => {
  if (text.length >= 2 && text.startsWith("[") && text.endsWith("]")) {
    return text.slice(1, -1);
  }
  return text;
};

/** Strips a single layer of matching quote-character wrapping (`'`, `"` or a backtick). */
const stripQuotes = (text: string): string => {
  if (text.length >= 2) {
    const first: string = text.charAt(0);
    const last: string = text.charAt(text.length - 1);
    const isQuoteChar: boolean = first === "'" || first === '"' || first === BACKTICK;
    if (isQuoteChar && first === last) {
      return text.slice(1, -1);
    }
  }
  return text;
};

/** Runs one global regex to completion against `message`, pushing each capture-group-1 match. */
const collectPatternMatches = (
  message: string,
  pattern: RegExp,
  candidates: IRawCandidate[],
  transform?: (raw: string) => string
): void => {
  pattern.lastIndex = 0;
  let match: RegExpExecArray | null = pattern.exec(message);
  while (match !== null) {
    const raw: string | undefined = match[1];
    if (raw !== undefined) {
      const text: string = transform ? transform(raw) : raw;
      candidates.push({ index: match.index, text });
    }
    match = pattern.exec(message);
  }
};

/** Runs every pattern against the raw message and collects unfiltered, unranked candidates. */
const collectRawCandidates = (message: string): IRawCandidate[] => {
  const candidates: IRawCandidate[] = [];

  for (const pattern of KEYWORD_QUOTE_PATTERNS) {
    collectPatternMatches(message, pattern, candidates);
  }
  collectPatternMatches(message, ORACLE_DOTTED_PATH_PATTERN, candidates);
  collectPatternMatches(message, ORACLE_INVALID_IDENTIFIER_PATTERN, candidates);
  collectPatternMatches(message, SQLSERVER_ALTER_COLUMN_PATTERN, candidates, stripWrappingBrackets);

  // The Postgres "Key (...)" group can list several comma-joined names in one match.
  POSTGRES_KEY_DUPLICATE_PATTERN.lastIndex = 0;
  let keyMatch: RegExpExecArray | null = POSTGRES_KEY_DUPLICATE_PATTERN.exec(message);
  while (keyMatch !== null) {
    const groupIndex: number = keyMatch.index;
    const inner: string = keyMatch[1];
    for (const part of inner.split(",")) {
      const name: string = stripQuotes(part.trim());
      if (name) {
        candidates.push({ index: groupIndex, text: name });
      }
    }
    keyMatch = POSTGRES_KEY_DUPLICATE_PATTERN.exec(message);
  }

  return candidates;
};

/**
 * The columns a failed migration's raw provider message names. Only names present in
 * `columnNames` count (compared case-insensitively, ordinal); the result uses the spelling from
 * `columnNames`, in order of first appearance in the message, deduped. Empty means "the text
 * names no known column".
 */
export const findColumnsInMigrationError = (
  message: string | null | undefined,
  columnNames: readonly string[]
): string[] => {
  if (!message) {
    return [];
  }

  const rawCandidates: IRawCandidate[] = collectRawCandidates(message);

  // A quoted identifier that is itself dotted (e.g. 'dbo.Orders.Name') is also tried by its last
  // segment, at the same position, right after the untouched original.
  const expanded: IRawCandidate[] = [];
  for (const candidate of rawCandidates) {
    const text: string = candidate.text.trim();
    if (!text) {
      continue;
    }
    expanded.push({ index: candidate.index, text });
    const lastDot: number = text.lastIndexOf(".");
    if (lastDot >= 0 && lastDot < text.length - 1) {
      const lastSegment: string = text.slice(lastDot + 1);
      if (lastSegment) {
        expanded.push({ index: candidate.index, text: lastSegment });
      }
    }
  }

  // Stable sort by position of first appearance in the message; pattern-iteration order never
  // leaks into the result.
  expanded.sort((a: IRawCandidate, b: IRawCandidate): number => a.index - b.index);

  const results: string[] = [];
  const addedLower: Set<string> = new Set<string>();
  for (const candidate of expanded) {
    const candidateLower: string = candidate.text.toLowerCase();
    const matchedColumn: string | undefined = columnNames.find(
      (name: string): boolean => name.toLowerCase() === candidateLower
    );
    if (matchedColumn && !addedLower.has(matchedColumn.toLowerCase())) {
      addedLower.add(matchedColumn.toLowerCase());
      results.push(matchedColumn);
    }
  }
  return results;
};
